import { isTombstone, LISTED_ROLES, memberRefs, picksIn, refNames, sectionNamed, sections } from './entity';
import type { VaultEntity } from './entity';

/**
 * What a surface may offer for this entity.
 *
 * The rule, and the reason this is derived rather than switched on a type:
 *
 * > A capability is whatever the holder's FILE can carry.
 *
 * Writing needs a file to append a block to; positions need a file to hold
 * the `plane:` key; membership needs a file that lists members. A synthetic
 * holder — an overview across arcs, an agenda — has none of those for the
 * same single reason, which is why it is not a special case but the general
 * rule with a null holder.
 *
 * Kept closed and small on purpose. SpaceDetailsScreen earned its
 * complexity over years of accumulation; this list should stay readable in
 * one screenful, and a new entry should have to argue for itself.
 */
export interface Capabilities {
  /** Declares a board status — appears in the plan, can be moved. */
  planned: boolean;
  /** Declares hand order within its status. */
  ordered: boolean;
  /** States an ending and a subject: `done-when` / `scope`. */
  scoped: boolean;
  /** Holds other entities: a storyline's `arcs:`, an arc's `sources:`, a
   *  workspace's sources and talk. */
  holdsMembers: boolean;
  /** Its body has anchored heading-sections — a recording's topics. */
  hasSections: boolean;
  /** Carries a `# Face` — the picked memory triggers. */
  hasFace: boolean;
  /** Carries a `## Suggestions` list the face was curated from. */
  hasSuggestions: boolean;
  /** Its body is a transcript in sections rather than one run of prose. */
  hasTranscript: boolean;
  /** Something can be written INTO this file. Needs a real file, which is
   *  the whole distinction from a synthetic holder. */
  writable: boolean;
  /** Can persist plane positions, for the same reason. */
  positionable: boolean;
  /** A deletion marker, not content. Everything else is false for one. */
  tombstone: boolean;
}

export const NO_CAPABILITIES: Capabilities = {
  planned: false,
  ordered: false,
  scoped: false,
  holdsMembers: false,
  hasSections: false,
  hasFace: false,
  hasSuggestions: false,
  hasTranscript: false,
  writable: false,
  positionable: false,
  tombstone: false,
};

export function capabilitiesOf(entity: VaultEntity | null): Capabilities {
  if (!entity) return NO_CAPABILITIES;
  // A tombstone offers nothing: it is the record of an absence, and a surface
  // that treats it as a file renders a blank page.
  if (isTombstone(entity)) return { ...NO_CAPABILITIES, tombstone: true };
  const fm = entity.frontMatter;
  const backedByAFile = entity.path.length > 0;

  return {
    planned: fm['status'] !== undefined,
    ordered: fm['order'] !== undefined,
    scoped: fm['done-when'] !== undefined || fm['scope'] !== undefined,
    holdsMembers:
      refNames(fm['arcs']).length > 0 ||
      refNames(fm['sources']).length > 0 ||
      memberRefs(entity.raw).some((ref) => LISTED_ROLES.has(ref.role ?? '')),
    hasSections: sections(entity).length > 0,
    hasFace: (picksIn(sectionNamed(entity, 'face')?.body ?? '')).length > 0,
    hasSuggestions: (picksIn(sectionNamed(entity, 'suggestions')?.body ?? '')).length > 0,
    hasTranscript: sectionNamed(entity, 'transcript') !== null,
    writable: backedByAFile,
    positionable: backedByAFile,
    tombstone: false,
  };
}
